import React from 'react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ArrowRightLeft, Package, ChevronDown, Archive, Layers, MapPin } from 'lucide-react';

export const PositionTransferDialog = ({
  open, onOpenChange, item, selectedNodeCode,
  dest, setDest, qty, setQty,
  cabinets, shelves, positions,
  bins, stockData, onConfirm
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5 text-blue-400" /> Transfera Articol
        </DialogTitle>
        <DialogDescription className="text-zinc-400">
          Selectati destinatia: Dulap &rarr; Raft &rarr; Pozitie
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        {item && (
          <div className="p-3 rounded-xl border border-blue-500/20 bg-blue-500/5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Package className="w-4 h-4 text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{item.article_name || item.article_code}</p>
                <p className="text-[10px] font-mono text-zinc-500">Din: {selectedNodeCode} &middot; Stoc: {item.quantity} {item.unit || 'buc'}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-center">
          <div className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center">
            <ChevronDown className="w-4 h-4 text-blue-400" />
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-zinc-400 text-xs mb-1.5 flex items-center gap-1"><Archive className="w-3 h-3 text-indigo-400" /> Dulap Destinatie</Label>
            <Select value={dest.cabinet} onValueChange={v => setDest({ cabinet: v, shelf: '', positionId: '' })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="pos-transfer-cabinet">
                <SelectValue placeholder="Selectati dulapul..." />
              </SelectTrigger>
              <SelectContent>
                {cabinets.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {dest.cabinet && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-200">
              <Label className="text-zinc-400 text-xs mb-1.5 flex items-center gap-1"><Layers className="w-3 h-3 text-green-400" /> Raft</Label>
              <Select value={dest.shelf} onValueChange={v => setDest({ ...dest, shelf: v, positionId: '' })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="pos-transfer-shelf">
                  <SelectValue placeholder="Selectati raftul..." />
                </SelectTrigger>
                <SelectContent>
                  {shelves.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {dest.shelf && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-200">
              <Label className="text-zinc-400 text-xs mb-1.5 flex items-center gap-1"><MapPin className="w-3 h-3 text-orange-400" /> Pozitie</Label>
              <div className="grid grid-cols-4 gap-1.5 p-2 rounded-lg bg-zinc-800 border border-zinc-700 max-h-40 overflow-y-auto" data-testid="pos-transfer-positions-grid">
                {positions.map(p => {
                  const isSource = p.id === (bins.find(b => b.code === selectedNodeCode)?.id);
                  const pStock = stockData.filter(s => s.bin_id === p.id && s.quantity > 0).length;
                  return (
                    <button key={p.id} disabled={isSource}
                      data-testid={`pos-transfer-pos-${p.position}`}
                      className={`p-2 rounded-lg border text-center text-xs font-mono transition-all
                        ${dest.positionId === p.id ? 'border-blue-500 bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/50 shadow-lg shadow-blue-500/10' : ''}
                        ${isSource ? 'border-zinc-700 bg-zinc-700/30 text-zinc-600 cursor-not-allowed' : ''}
                        ${!isSource && dest.positionId !== p.id ? 'border-zinc-700 hover:border-zinc-500 hover:bg-zinc-700/50 text-zinc-300 cursor-pointer' : ''}
                      `}
                      onClick={() => !isSource && setDest({ ...dest, positionId: p.id })}>
                      <span className="font-bold">{p.position}</span>
                      {pStock > 0 && <span className="block text-[9px] text-emerald-400 mt-0.5">{pStock} art.</span>}
                      {pStock === 0 && !isSource && <span className="block text-[9px] text-zinc-600 mt-0.5">gol</span>}
                      {isSource && <span className="block text-[9px] text-zinc-600 mt-0.5">sursa</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {dest.positionId && (
            <div className="p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 animate-in fade-in duration-200">
              <p className="text-[10px] text-zinc-500 mb-1">Destinatie selectata:</p>
              <p className="font-mono font-bold text-emerald-400 text-sm">
                {dest.cabinet} &rarr; {dest.shelf} &rarr; {bins.find(b => b.id === dest.positionId)?.position || '?'}
              </p>
            </div>
          )}

          {dest.positionId && item && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-200">
              <Label className="text-zinc-400 text-xs mb-1.5">Cantitate (max {item.quantity})</Label>
              <input type="number" min="0.01" max={item.quantity} step="1"
                value={qty}
                onChange={e => setQty(Math.min(parseFloat(e.target.value) || 0, item.quantity))}
                className="w-full h-10 px-3 text-lg font-mono text-center rounded-lg bg-zinc-800 border border-zinc-600 text-white outline-none focus:border-blue-500"
                data-testid="pos-transfer-qty" />
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" className="text-zinc-400" onClick={() => onOpenChange(false)}>Anuleaza</Button>
        <Button className="bg-blue-600 hover:bg-blue-700"
          disabled={!dest.positionId || qty <= 0}
          onClick={onConfirm}
          data-testid="pos-transfer-confirm">
          <ArrowRightLeft className="w-4 h-4 mr-1" />Transfera {qty > 0 ? qty : ''}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
